import Listing from '@lib/models/listing';
import { fetchAssociatedAgent, getAssociatedAgentItem } from '@lib/associatedAgent';
import { generateSearchResultsSchema } from '@lib/generate-schemas';
import { getOrSetCache } from '@lib/server-cache';
import communityLookup from '@src/data/community-info.json';
import builderDevelopments from '@src/data/builder-development-info.json';
import ListingDetailPageTemplate from '@templates/ListingDetailPage';

// ---------------------------------------------------------

// Interest rates are global (identical for every listing) and change ~daily, so
// they're cached to avoid two REST calls on every listing view.
const INTEREST_RATE_TTL = 60 * 60 * 1000; // 1 hour

// ---------------------------------------------------------

const getCanonicalSlug = (fullCanonical) => {
  const canonicalPath = new URL(fullCanonical).pathname;
  const parts = canonicalPath.split('/');
  const slug = parts.slice(3).join('/');
  return slug;
};

export async function getServerSideProps(context) {
  const notFoundPage = { notFound: true };

  // set caching headers to avoid caching in the browser
  context.res.setHeader('Cache-Control', 'no-store');

  const { id: slugId, slug: slugArray } = context.params;
  const possibleListingId = parseInt(slugId, 10);
  const listingId = isNaN(possibleListingId) ? 0 : possibleListingId;

  // 404 if listing id missing
  if (listingId <= 0) return notFoundPage;

  const listing = new Listing(listingId);
  const details = await listing.details();

  // 404 if no pageData for the ID
  if (!details) return notFoundPage;

  // Redirect if the canonical URL doesn't match
  const { canonicalUrl } = details;
  const canonicalFromAPI = getCanonicalSlug(canonicalUrl);

  let slugAddress;
  if (slugArray?.length > 1) {
    slugAddress = slugArray.join('/');
  } else if (slugArray?.length === 1) {
    slugAddress = slugArray[0];
  } else {
    slugAddress = null;
  }

  let slugCheck = encodeURIComponent(slugAddress);

  // Apostrophe Edge Case
  slugCheck = slugCheck.replace("'", '%27');

  if (canonicalFromAPI !== slugCheck) {
    const { resolvedUrl } = context;

    let destinationUrl;
    if (slugAddress) {
      destinationUrl = resolvedUrl.replace(slugCheck, canonicalFromAPI);
    } else {
      destinationUrl = `/listing/${listingId}/${canonicalFromAPI}`;
      const queryString = resolvedUrl.split('?')[1];
      if (queryString) {
        destinationUrl += `?${queryString}`;
      }
    }

    return {
      redirect: {
        destination: destinationUrl,
        permanent: false
      }
    };
  }

  const fullCommunityLookup = Object.entries(communityLookup).map((x) => ({
    lookups: (x[1].fallbackSlugs || []).concat([x[0]]),
    slug: x[0]
  }));

  const listingCommunity =
    communityLookup[
      fullCommunityLookup.find((x) => x.lookups.some((y) => y === details?.communitySlug))?.slug ||
        details.communityFallbackSlug
    ];

  const builder =
    builderDevelopments.find((x) => x.category == 'Builder' && x.slug == details.builderSlug) || {};
  builder.url = `/builder/${builder?.slug}`;
  const development =
    builderDevelopments.find(
      (x) => x.category == 'Development' && x.slug == details.developmentSlug
    ) || {};
  development.url = `/development/${development?.slug}`;

  const nearbyCommunities = listingCommunity.nearbyCommunities.map((slug) => communityLookup[slug]);

  const { agentid: queryAgentId } = context.query;
  const possibleAgentId = parseInt(queryAgentId, 10);
  const agentId = isNaN(possibleAgentId) ? 0 : possibleAgentId;

  // These calls are independent of one another (they only depend on `details`),
  // so run them concurrently instead of sequentially. Interest rates are cached
  // globally since they're the same for every listing.
  const [similarProperties, thirtyYearRate, fifteenYearRate, associatedAgent] = await Promise.all([
    listing.getSimilarListings(details?.similarListingIds),
    getOrSetCache('interest-rate:30', INTEREST_RATE_TTL, () => listing.interest(30)),
    getOrSetCache('interest-rate:15', INTEREST_RATE_TTL, () => listing.interest(15)),
    agentId > 0 ? fetchAssociatedAgent({ agentId }) : Promise.resolve(null)
  ]);

  const interestRates = {
    15: fifteenYearRate?.currentRate || '6',
    30: thirtyYearRate?.currentRate || '5.5',
    lastUpdatedOn: thirtyYearRate?.lastUpdatedOn || new Date()
  };

  // Generate similar properties schema when available
  let similarPropertiesSchema = null;
  if (similarProperties?.length > 0) {
    const { address, city, state, zip } = details;
    similarPropertiesSchema = generateSearchResultsSchema({
      name: `Properties Similar to ${address}, ${city}, ${state} ${zip}`,
      listings: similarProperties
    });
  }

  return {
    props: {
      ...details,
      builder,
      development,
      interestRates,
      listingCommunity,
      nearbyCommunities,
      similarProperties,
      similarPropertiesSchema,
      associatedAgent: getAssociatedAgentItem(associatedAgent)
    }
  };
}

// ---------------------------------------------------------

const ListingDetail = (props) => {
  return <ListingDetailPageTemplate {...props} />;
};

export default ListingDetail;
